<template>
  <v-app :class="{ 'theme--dark': globalTheme }">
    <!-- Layout component -->
    <component :is="currentLayout" v-if="isRouterLoaded">
      <transition name="fade" mode="out-in">
        <router-view />
      </transition>
    </component>

    <!-- progressbar -->
    <vue-progress-bar></vue-progress-bar>

    <!-- Snackbar Toast -->
    <v-snackbar v-model="toast.show" :timeout="toast.timeout" :color="toast.color" bottom right>
      {{ toast.message }}
      <v-btn v-if="toast.timeout === 0" color="white" text @click="toast.show = false">{{ $t('common.close') }}</v-btn>
    </v-snackbar>

    <!-- Aviso de nueva versión disponible -->
    <v-snackbar v-model="updateAvailable" :timeout="-1" color="primary" bottom right>
      <div style="position: relative; padding: 2px 28px 2px 0; min-width: 200px;">
        <v-btn icon x-small @click="updateAvailable = false" :aria-label="$t('common.close')"
               style="position: absolute; top: -2px; right: -6px;">
          <v-icon x-small color="white">mdi-close</v-icon>
        </v-btn>
        <div class="d-flex align-center mb-2">
          <v-icon left small color="white">mdi-update</v-icon>
          <span>{{ $t('common.updateAvailable') }}</span>
        </div>
        <v-btn color="white" outlined small block @click="reloadApp">{{ $t('common.reload') }}</v-btn>
      </div>
    </v-snackbar>

    <!-- Aviso en vivo de tickets: comentario en tu reservado / te quitaron la reserva -->
    <TicketRealtimeNotification />

  </v-app>
</template>

<script>
import { mapState, mapGetters , mapActions } from 'vuex'

import config from './configs'
import TicketRealtimeNotification from '@/components/tickets/shared/TicketRealtimeNotification.vue'

// Layouts
import defaultLayout from './layouts/DefaultLayout'
import simpleLayout from './layouts/SimpleLayout'
import landingLayout from './layouts/LandingLayout'
import authLayout from './layouts/AuthLayout'
import errorLayout from './layouts/ErrorLayout'
/*
|---------------------------------------------------------------------
| Main Application Component
|---------------------------------------------------------------------
|
| In charge of choosing the layout according to the router metadata
|
*/
export default {
  components: {
    defaultLayout,
    simpleLayout,
    landingLayout,
    authLayout,
    errorLayout,
    TicketRealtimeNotification
  },
  data: () => ({
    updateAvailable: false
  }),
  computed: {
    ...mapState('app', ['toast', 'globalTheme', 'themeMode']),
    ...mapGetters('counters', ['shouldRefreshCounters']),
    isRouterLoaded: function() {
      if (this.$route.name !== null) return true

      return false
    },
    currentLayout: function() {
      const layout = this.$route.meta.layout || 'default'

      return layout + 'Layout'
    }
  },
  head: {
    link: [
      // adds config/icons into the html head tag
      ...config.icons.map((href) => ({ rel: 'stylesheet', href }))
    ]
  },

  async created() {
    // Carga inicial única de contadores (todos vienen de /system-counter,
    // incluido el contador por-usuario pre_sofonos_mios).
    await this.loadCountersIfNeeded();

    // Conectar WebSockets si hay sesión persistida (recarga de página) y
    // suscribirse a la actualización en tiempo real de los badges (global) y
    // de reserva/comentario de tickets (global — el aviso de comentario debe
    // funcionar en cualquier pantalla, no sólo con el listado abierto).
    this.$store.dispatch('initEcho');
    this.subscribeCounters();
    this.subscribeTicketsRealtime();
    this.subscribeOTRealtime();

    // Fallback de baja frecuencia por si Echo no está disponible; también
    // mantiene fresco el contador por-usuario (no se emite por broadcast).
    this.startCounterRefreshInterval();
  },

  beforeDestroy() {
    this.unsubscribeCounters();
    this.unsubscribeTicketsRealtime();
    this.unsubscribeOTRealtime();
    this.stopCounterRefreshInterval();
    if (this.darkModeMediaQuery) {
      if (this.darkModeMediaQuery.removeEventListener) {
        this.darkModeMediaQuery.removeEventListener('change', this.handleOsThemeChange);
      } else {
        // Safari < 14 fallback
        this.darkModeMediaQuery.removeListener(this.handleOsThemeChange);
      }
    }
    window.removeEventListener('appUpdateAvailable', this.onAppUpdateAvailable);
  },
  mounted() {
    // Apply the persisted theme mode (os | light | dark) on startup
    this.applyTheme();

    // While in "os" mode, keep the theme in sync with OS changes live
    if (window.matchMedia) {
      this.darkModeMediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
      if (this.darkModeMediaQuery.addEventListener) {
        this.darkModeMediaQuery.addEventListener('change', this.handleOsThemeChange);
      } else {
        // Safari < 14 fallback
        this.darkModeMediaQuery.addListener(this.handleOsThemeChange);
      }
    }

    // Lo emiten tanto versionCheck.js (chequeo de /version.json) como
    // registerServiceWorker.js. Ver la nota en versionCheck.js sobre por qué el
    // service worker por sí solo dejó de detectar despliegues.
    this.onAppUpdateAvailable = () => { this.updateAvailable = true };
    window.addEventListener('appUpdateAvailable', this.onAppUpdateAvailable);
  },
  methods:{
    ...mapActions('app', ['setGlobalTheme', 'applyTheme']),
    ...mapActions('counters', ['loadCounters', 'refreshCounters', 'subscribeCounters', 'unsubscribeCounters']),
    ...mapActions('ticketsRealtime', ['subscribeTicketsRealtime', 'unsubscribeTicketsRealtime']),
    ...mapActions('ordenesTrabajoRealtime', ['subscribeOTRealtime', 'unsubscribeOTRealtime']),

    handleOsThemeChange(event) {
      // Only follow the OS when the user hasn't forced a specific mode
      if (this.themeMode === 'os') {
        this.setGlobalTheme(event.matches);
      }
    },

    reloadApp() {
      this.updateAvailable = false;
      // Reutiliza la recuperación determinista (desregistrar SW + limpiar caches)
      // para que el banner nunca quede atrapado sirviendo el index.html obsoleto.
      // force: es una acción explícita del usuario, no debe quedar bloqueada por
      // el guard de una sola pasada de chunkReloadGuard.
      import('./utils/chunkReloadGuard').then(m => m.recoverAndReload({ force: true }))
                                        .catch(() => window.location.reload());
    },

    async loadCountersIfNeeded() {
      try {
        await this.loadCounters(false); // false = usa cache si está fresco
      } catch (error) {
        console.error('Error inicializando contadores:', error);
      }
    },

    startCounterRefreshInterval() {
      // Fallback: refrescar cada 10 minutos (cuando Echo no está activo).
      this.refreshInterval = setInterval(async () => {
        try {
          await this.refreshCounters();
        } catch (error) {
          console.error('Error refrescando contadores:', error);
        }
      }, 10 * 60 * 1000);
    },

    stopCounterRefreshInterval() {
      if (this.refreshInterval) {
        clearInterval(this.refreshInterval);
      }
    }
  }
}
</script>

<style scoped>
/**
 * Transition animation between pages
 */
.fade-enter-active,
.fade-leave-active {
  transition-duration: 0.2s;
  transition-property: opacity;
  transition-timing-function: ease;
}

.fade-enter,
.fade-leave-active {
  opacity: 0;
}




/* body {
  background-color: #ffffff;
  color: #000000;
}

.v-application--is-ltr.theme--dark {
  background-color: #121212;
  color: #ffffff;
} */
</style>
